import { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { CheckDouble as CheckDoubleIcon, Cross as CrossIcon } from '@factorialco/f0-react/icons/app'
import {
  BulkApproveApprovalsv2ReviewRequestDocument,
  BulkRejectApprovalsv2ReviewRequestDocument,
  ExpenseDetailPageDocument,
  ExpenseDetailPageQuery,
  ExpensesEntitiesExpensableResourceTypeEnum,
} from 'generated/resources/graphql'

import Footer from 'modules/core/components/Sidepanel/Footer'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { type ExpenseFormValues } from 'modules/expenses/components/expenses/Detail/_Form/Expense/new/form/form'
import { type MileageFormValues } from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/form/form'
import { type PerDiemFormValues } from 'modules/expenses/components/expenses/Detail/_Form/PerDiem/new/form/form'
import ApproveExpensableButton from 'modules/expenses/components/expenses/Form/Buttons/ApproveExpensableButton'
import DeleteExpensableButtons from 'modules/expenses/components/expenses/Form/Buttons/DeleteExpensableButtons'
import ReimburseExpensableThroughSepaButton from 'modules/expenses/components/expenses/Form/Buttons/ReimburseExpensableThroughSepaButton'
import RejectExpensableButton from 'modules/expenses/components/expenses/Form/Buttons/RejectExpensableButton'
import SendExpensableForApprovalButton from 'modules/expenses/components/expenses/Form/Buttons/SendExpensableForApprovalButton'
import ToggleExpensablePaidStatusButton from 'modules/expenses/components/expenses/Form/Buttons/ToggleExpensablePaidStatusButton'
import ToggleExpensablePayrollStatusButton from 'modules/expenses/components/expenses/Form/Buttons/ToggleExpensablePayrollStatusButton'
import UpdateButton from 'modules/expenses/components/expenses/Form/Buttons/UpdateButton'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { SignableOrganizationPerCountry } from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import {
  isAutomaticExpensable,
  isChangesRequestedExpensable,
  isDeletableExpensable,
  isDraftExpensable,
  isExpensableReporter,
  isOwnExpensable,
  isPendingExpensable,
} from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'

type ApprovalProcess =
  ExpenseDetailPageQuery['expenses']['expensablesConnection']['edges'][number]['node']['approvalProcess']

interface EditExpenseFooterActionsProps {
  formType: ExpensesEntitiesExpensableResourceTypeEnum
  expensable: Expensable
  formValues: ExpenseFormValues | PerDiemFormValues | MileageFormValues
  customizationFields: CustomizationField[]
  canEdit: boolean
  canOverrideApprovalFlow: boolean
  canDeleteExpensable: boolean
  canSetExpensableToPaid: boolean
  reimburseThroughPayrollEnabled: boolean
  reimburseThroughSepaEnabled: boolean
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  formValid?: boolean
  formDirty?: boolean
  approvalsV2Enabled: boolean
  approvalProcess: ApprovalProcess | null

  trigger: () => Promise<boolean>
  onClose: () => void
}

const EditExpenseFooterActions = ({
  formType,
  expensable,
  formValues,
  customizationFields,
  canEdit,
  canOverrideApprovalFlow,
  canDeleteExpensable,
  canSetExpensableToPaid,
  reimburseThroughPayrollEnabled,
  reimburseThroughSepaEnabled,
  taxAgencyOrganization,
  formValid,
  formDirty,
  approvalsV2Enabled,
  approvalProcess,
  trigger,
  onClose,
}: EditExpenseFooterActionsProps) => {
  const [isUpdating, setUpdating] = useState(false)
  const [isSending, setSending] = useState(false)
  const [isDeleting, setDeleting] = useState(false)
  const [isRejecting, setRejecting] = useState(false)
  const [isApproving, setApproving] = useState(false)
  const [isPaidStatusChanging, setPaidStatusChanging] = useState(false)
  const [isPayrollStatusChanging, setPayrollStatusChanging] = useState(false)
  const [isSepaStatusChanging, setSepaStatusChanging] = useState(false)

  // Reset all button states when expensable changes
  // We want to avoid stale states in flows where the sidepanel does not close
  // Flows such as "restart approval flow" or "set as paid"
  useEffect(() => {
    setUpdating(false)
    setSending(false)
    setDeleting(false)
    setRejecting(false)
    setApproving(false)
    setPaidStatusChanging(false)
    setPayrollStatusChanging(false)
    setSepaStatusChanging(false)
  }, [expensable])

  const { section, expensableGroupsEnabled } = useOutletContext<RouteContextParams>()
  const { employee: currentEmployee, company } = useCurrentSession()
  const apolloClient = useApolloClient()

  const [bulkApprove] = useMutation(BulkApproveApprovalsv2ReviewRequestDocument, {
    extractorFn: (data) => data.approvalsV2Mutations.bulkApproveApprovalsv2ReviewRequest,
  })

  const [bulkReject] = useMutation(BulkRejectApprovalsv2ReviewRequestDocument, {
    extractorFn: (data) => data.approvalsV2Mutations.bulkRejectApprovalsv2ReviewRequest,
  })

  // Extract review request IDs from approval process
  const reviewRequestIds = useUnsafeMemo(
    () => approvalProcess?.reviewRequests?.map((reviewRequest) => reviewRequest.id) || [],
    [approvalProcess?.reviewRequests]
  )

  const handleApprove = useUnsafeCallback(async () => {
    if (!reviewRequestIds.length || !company?.id) return

    setApproving(true)
    try {
      const result = await bulkApprove({
        variables: {
          companyId: company.id,
          reviewRequestIds,
          reviewerId: currentEmployee.id,
        },
      })

      const handled = handleResult(result, () => {})

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('settings.approvals.approval_action.flash_messages.approve'),
        })
        // Refetch the expense detail page to update the UI
        await apolloClient.refetchQueries({
          include: [ExpenseDetailPageDocument],
        })
        onClose()
      }
    } finally {
      setApproving(false)
    }
  }, [reviewRequestIds, company?.id, currentEmployee.id, bulkApprove, apolloClient, onClose])

  const handleReject = useUnsafeCallback(async () => {
    if (!reviewRequestIds.length || !company?.id) return

    setRejecting(true)
    try {
      const result = await bulkReject({
        variables: {
          companyId: company.id,
          reviewRequestIds,
          reviewerId: currentEmployee.id,
          reason: undefined, // TODO: Add reason input if needed
        },
      })

      const handled = handleResult(result, () => {})

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('settings.approvals.approval_action.flash_messages.reject'),
        })
        // Refetch the expense detail page to update the UI
        await apolloClient.refetchQueries({
          include: [ExpenseDetailPageDocument],
        })
        onClose()
      }
    } finally {
      setRejecting(false)
    }
  }, [reviewRequestIds, company?.id, currentEmployee.id, bulkReject, apolloClient, onClose])

  const isDraft = isDraftExpensable(expensable)
  const isPending: boolean = isPendingExpensable(expensable)
  const isChangesRequested = isChangesRequestedExpensable(expensable)

  const isOwner = isOwnExpensable(expensable, currentEmployee)
  const isReporter = isExpensableReporter(expensable, currentEmployee)

  const isCardPaymentPending = expensable?.expense?.cardPayment?.status === 'pending'
  const isAutomaticExpensePending = isAutomaticExpensable(expensable) && isCardPaymentPending
  const isPendingApproval =
    isPending && expensable.currentUserApprovalStatus === 'pending' && !isCardPaymentPending
  const isOwnerOrReporter = isOwner || isReporter

  const expenseIsReviewable = isPendingApproval && !isMyExpensesSection(section)
  const canReviewWithOverridePermission =
    isPending && canOverrideApprovalFlow && !isAutomaticExpensePending

  const isReviewable = expenseIsReviewable || canReviewWithOverridePermission
  const showSendForApproval =
    (isDraft || isChangesRequested) && canEdit && !(expensable.groupId && expensableGroupsEnabled)

  const isSubmitting =
    isUpdating ||
    isSending ||
    isDeleting ||
    isRejecting ||
    isApproving ||
    isPaidStatusChanging ||
    isPayrollStatusChanging ||
    isSepaStatusChanging

  const isReporterOrOwnerAndDraft = isOwnerOrReporter && isDraft
  const isOwnerAndChangesRequested = isOwner && isChangesRequested
  const canBeEditedAndIsPending = canEdit && isPending
  // TODO: Try to move into policy
  const showSaveButton =
    isReporterOrOwnerAndDraft || isOwnerAndChangesRequested || canBeEditedAndIsPending

  const showDelete =
    isDeletableExpensable(expensable, canDeleteExpensable, currentEmployee) || expensable.groupId

  const canReviewStep = expensable.currentUserApprovalStatus == 'pending'
  const showApproveButton =
    (canReviewStep || canOverrideApprovalFlow) && isPending && !isCardPaymentPending

  const canUpdateAndApprove = canBeEditedAndIsPending && formDirty

  const showRejectButton = isPending && (canReviewStep || canOverrideApprovalFlow)

  const canRequestChanges = canReviewStep || !canOverrideApprovalFlow

  const showToggleExpensablePaidStatusButton =
    (expensable.status == 'approved' || expensable.status == 'paid') && canSetExpensableToPaid

  const showToggleExpensablePayrollStatusButton =
    (expensable.status === 'approved' || expensable.status === 'in_payroll') &&
    reimburseThroughPayrollEnabled

  const showReimburseThroughSepaButton =
    expensable.status === 'approved' &&
    reimburseThroughSepaEnabled &&
    !reimburseThroughPayrollEnabled

  const canAnyAction =
    showSaveButton ||
    showDelete ||
    showRejectButton ||
    showSendForApproval ||
    showApproveButton ||
    showToggleExpensablePaidStatusButton ||
    showToggleExpensablePayrollStatusButton ||
    showReimburseThroughSepaButton

  if (!canAnyAction) {
    return null
  }

  return (
    <Box aria-label="footer-actions" position="sticky" style={{ bottom: '0' }}>
      <Footer
        content={
          <Box
            width="full"
            flexDirection="row"
            justifyContent={canEdit ? 'spaceBetween' : 'flexEnd'}
            alignItems="center"
            paddingX="s8"
            gap="s8"
          >
            <Box flexDirection="row" gap="s8">
              {showSaveButton && (
                <UpdateButton
                  formType={formType}
                  expensable={expensable}
                  formIsDirty={formDirty ?? false}
                  formValues={formValues}
                  taxAgencyOrganization={taxAgencyOrganization}
                  customizationFields={customizationFields}
                  canBeReviewed={isReviewable}
                  onClose={onClose}
                  onLoadingChange={setUpdating}
                  disabled={isSubmitting}
                />
              )}
              {showDelete && (
                <DeleteExpensableButtons
                  canDeleteExpensable={canDeleteExpensable}
                  expensable={expensable}
                  onClose={onClose}
                  onLoadingChange={setDeleting}
                  disabled={isSubmitting}
                />
              )}
            </Box>
            <Box flexDirection="row" gap="s8">
              {showRejectButton &&
                (approvalsV2Enabled && approvalProcess ? (
                  <F0Button
                    label={i18n.t('common.reject')}
                    icon={CrossIcon}
                    variant="critical"
                    disabled={isSubmitting || !reviewRequestIds.length}
                    onClick={handleReject}
                  />
                ) : (
                  <RejectExpensableButton
                    expensable={expensable}
                    canOverrideApprovalFlow={canOverrideApprovalFlow}
                    canRequestChanges={canRequestChanges}
                    onLoadingChange={setRejecting}
                    disabled={isSubmitting}
                  />
                ))}
              {showSendForApproval && (
                <SendExpensableForApprovalButton
                  formType={formType}
                  customizationFields={customizationFields}
                  formValues={formValues}
                  formValid={formValid ?? false}
                  expensable={expensable}
                  taxAgencyOrganization={taxAgencyOrganization}
                  onClose={onClose}
                  trigger={trigger}
                  onLoadingChange={setSending}
                  disabled={isSubmitting}
                />
              )}

              {showApproveButton &&
                (approvalsV2Enabled && approvalProcess ? (
                  <F0Button
                    label={i18n.t('common.approve')}
                    icon={CheckDoubleIcon}
                    disabled={isSubmitting || !reviewRequestIds.length}
                    onClick={handleApprove}
                  />
                ) : (
                  <ApproveExpensableButton
                    formType={formType}
                    expensable={expensable}
                    canOverrideApprovalFlow={canOverrideApprovalFlow}
                    canUpdateAndApprove={canUpdateAndApprove}
                    formValues={formValues}
                    formDirty={formDirty}
                    formValid={formValid}
                    customizationFields={customizationFields}
                    taxAgencyOrganization={taxAgencyOrganization}
                    trigger={trigger}
                    onClose={onClose}
                    onLoadingChange={setApproving}
                    disabled={isSubmitting}
                  />
                ))}
            </Box>
            {showToggleExpensablePaidStatusButton && (
              <ToggleExpensablePaidStatusButton
                expensable={expensable}
                canSetToPaid={canSetExpensableToPaid}
                canReimburseThroughPayroll={showToggleExpensablePayrollStatusButton}
                onLoadingChange={setPaidStatusChanging}
                disabled={isSubmitting}
              />
            )}
            {showToggleExpensablePayrollStatusButton && (
              <ToggleExpensablePayrollStatusButton
                expensable={expensable}
                reimburseThroughPayrollEnabled={reimburseThroughPayrollEnabled}
                reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
                onLoadingChange={setPayrollStatusChanging}
                disabled={isSubmitting}
              />
            )}
            {showReimburseThroughSepaButton && (
              <ReimburseExpensableThroughSepaButton
                expensable={expensable}
                reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
                onLoadingChange={setSepaStatusChanging}
                disabled={isSubmitting}
              />
            )}
          </Box>
        }
      />
    </Box>
  )
}

export default EditExpenseFooterActions
